import React from "react";
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningIcon from "@mui/icons-material/Warning";

// Keyframe animations
const floatAnimation = `
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }
`;

const pulseAnimation = `
  @keyframes pulse {
    0%, 100% {
      opacity: 0.4;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.1);
    }
  }
`;

const rotateAnimation = `
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Hero = () => {
  const theme = useTheme();

  const stats = [
    {
      icon: <PeopleIcon sx={{ fontSize: 32, color: "primary.main" }} />,
      value: "24",
      label: "Active Staff",
      trend: "+3 from yesterday",
      trendColor: "success.main",
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 32, color: "primary.main" }} />,
      value: "94%",
      label: "On-Time Rate",
      trend: "+2% this week",
      trendColor: "success.main",
    },
    {
      icon: <WarningIcon sx={{ fontSize: 32, color: "warning.main" }} />,
      value: "3",
      label: "Active Alerts",
      trend: "Requires attention",
      trendColor: "text.secondary",
    },
  ];

  const staffData = [
    {
      name: "Sarah Johnson",
      status: "On Time",
      statusColor: "success",
      time: "8:58 AM",
    },
    {
      name: "Mike Chen",
      status: "Delayed",
      statusColor: "warning",
      time: "9:12 AM",
    },
    {
      name: "Emma Wilson",
      status: "On Location",
      statusColor: "success",
      time: "9:00 AM",
    },
  ];

  return (
    <Box
      id="hero"
      sx={{
        pt: { xs: 6, md: 12 },
        pb: { xs: 8, md: 12 },
        background: "linear-gradient(180deg, #F8F9FA 0%, #FFFFFF 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Inject animations into the document */}
      <style>
        {floatAnimation}
        {pulseAnimation}
        {rotateAnimation}
      </style>

      {/* Animated Background Patterns */}
      {/* Floating Circles */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(111, 45, 189, 0.15) 0%, rgba(111, 45, 189, 0) 70%)",
          animation: "float 6s ease-in-out infinite",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "60%",
          right: "10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(155, 93, 229, 0.12) 0%, rgba(155, 93, 229, 0) 70%)",
          animation: "float 8s ease-in-out infinite",
          animationDelay: "1s",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "5%",
          left: "15%",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(180, 99, 218, 0.1) 0%, rgba(180, 99, 218, 0) 70%)",
          animation: "float 7s ease-in-out infinite",
          animationDelay: "2s",
          zIndex: 0,
        }}
      />

      {/* Animated Grid Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            linear-gradient(rgba(111, 45, 189, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(111, 45, 189, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.5,
          zIndex: 0,
        }}
      />

      {/* Rotating Gradient Orbs */}
      <Box
        sx={{
          position: "absolute",
          top: "20%",
          right: "20%",
          width: "150px",
          height: "150px",
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(111, 45, 189, 0.2), rgba(155, 93, 229, 0.1))",
          animation:
            "pulse 4s ease-in-out infinite, rotate 20s linear infinite",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "20%",
          right: "5%",
          width: "100px",
          height: "100px",
          borderRadius: "50%",
          background:
            "linear-gradient(225deg, rgba(180, 99, 218, 0.15), rgba(111, 45, 189, 0.05))",
          animation:
            "pulse 5s ease-in-out infinite, rotate 15s linear infinite reverse",
          animationDelay: "1s",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Left Column - Text Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ maxWidth: 600 }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  fontSize: { xs: "2.25rem", md: "3rem", lg: "3.5rem" },
                  fontWeight: 800,
                }}
              >
                Monitor Your Workforce.
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontSize: { xs: "2.25rem", md: "3rem", lg: "3.5rem" },
                  fontWeight: 800,
                  background: theme.palette.gradient.main,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                In Real Time.
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: 4,
                  lineHeight: 1.7,
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontWeight: 400,
                }}
              >
                Smart scheduling, live tracking, and actionable insights for
                modern teams. Streamline operations and boost productivity.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: theme.palette.gradient.main,
                    color: "white",
                    px: 4,
                    py: 1.5,
                    borderRadius: "24px",
                    fontSize: "1.125rem",
                    "&:hover": {
                      background: theme.palette.gradient.reverse,
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    px: 4,
                    py: 1.5,
                    borderRadius: "24px",
                    fontSize: "1.125rem",
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                      backgroundColor: "primary.main",
                      color: "white",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  View Demo
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right Column - Dashboard Mockup */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: "relative" }}>
              {/* Top Row - Stat Cards */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {stats.map((stat, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Card
                      elevation={2}
                      sx={{
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: { xs: "1.75rem", sm: "2rem" },
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: stat.trendColor,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                          }}
                        >
                          {stat.trend}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Middle Row - Chart Placeholder */}
              <Paper
                elevation={2}
                sx={{
                  height: 200,
                  mb: 2,
                  background:
                    "linear-gradient(135deg, rgba(155, 93, 229, 0.1) 0%, rgba(255, 255, 255, 1) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Decorative Background Pattern */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    opacity: 0.05,
                    backgroundImage:
                      "repeating-linear-gradient(0deg, #6F2DBD 0px, transparent 1px, transparent 20px)",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    zIndex: 1,
                  }}
                >
                  Performance Trends
                </Typography>
              </Paper>

              {/* Bottom Row - Staff Table */}
              <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                  <TableBody>
                    {staffData.map((staff, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {staff.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={staff.status}
                            size="small"
                            color={staff.statusColor}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {staff.time}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
